import { FC, useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import * as Yup from 'yup';
import { shallow } from 'zustand/shallow';
import { useFormik } from 'formik';
import { LOCAL_STORAGE_TOKEN_KEY } from '../../../common/constants';
import Button from '../../../components/bootstrap/Button';
import Card, { CardBody } from '../../../components/bootstrap/Card';
import Spinner from '../../../components/bootstrap/Spinner';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Input from '../../../components/bootstrap/forms/Input';
import useDarkMode from '../../../hooks/useDarkMode';
import Page from '../../../layout/Page/Page';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import { useUserStore } from '../../../stores/useUserStore';
import logo from '../../../assets/img/logo.png';
import './Login.scss';

const Login: FC = () => {
	const { login } = useUserStore((state) => ({ user: state.user, login: state.login }), shallow);
	const { darkModeStatus } = useDarkMode();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [showPassword, setShowPassword] = useState<boolean>(false);

	const formik = useFormik({
		enableReinitialize: true,
		initialValues: {
			loginUsername: '',
			loginPassword: '',
		},
		validationSchema: Yup.object({
			loginUsername: Yup.string().required('Inserisci la username'),
			loginPassword: Yup.string().required('Inserisci la password'),
		}),
		validateOnChange: true,
		validateOnBlur: true,
		onSubmit: async (values) => {
			setIsLoading(true);
			try {
				await login({ username: values.loginUsername, password: values.loginPassword });
			} catch (err: any) {
				formik.setFieldError('loginUsername', err.message);
			} finally {
				setIsLoading(false);
			}
		},
	});

	if (localStorage.getItem(LOCAL_STORAGE_TOKEN_KEY)) {
		return <Navigate to='/homepage' />;
	}

	return (
		<PageWrapper isProtected={false} title='Login' className={`login-page bg-dark`}>
			<Page className='p-0'>
				<div className='login-container'>
					<div className='login-card-wrapper'>
						<Card className={`login-card ${darkModeStatus ? 'dark' : 'light'}`}>
							<CardBody className='p-4 p-md-5'>
								<div className='text-center mb-4'>
									<img src={logo} alt='Logo' className='login-logo' />
									<h2 className='mt-4 mb-1 fw-bold'>Benvenuto</h2>
									<p className='text-muted'>Accedi al tuo account per continuare</p>
								</div>

								<form className='login-form' onSubmit={formik.handleSubmit}>
									<div className='mb-4'>
										<FormGroup
											id='loginUsername'
											isFloating
											label='Username'
											className={formik.errors.loginUsername ? 'is-invalid' : ''}>
											<Input
												autoComplete='username'
												type='text'
												value={formik.values.loginUsername}
												isTouched={formik.touched.loginUsername}
												invalidFeedback={formik.errors.loginUsername}
												isValid={formik.isValid}
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
												onFocus={() => {
													formik.setErrors({});
												}}
												className='login-input'
											/>
										</FormGroup>
									</div>

									<div className='mb-4'>
										<FormGroup
											id='loginPassword'
											isFloating
											label='Password'
											className={formik.errors.loginPassword ? 'is-invalid' : ''}>
											<Input
												id='loginPassword'
												name='loginPassword'
												type={showPassword ? 'text' : 'password'}
												autoComplete='current-password'
												value={formik.values.loginPassword}
												invalidFeedback={formik.errors.loginPassword}
												isTouched={formik.touched.loginPassword}
												isValid={formik.isValid}
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
												onFocus={() => {
													formik.setErrors({});
												}}
												className='login-input password-input'
											/>
										</FormGroup>
									</div>

									<Button
										color='primary'
										className='login-button w-100'
										isDisable={
											isLoading || !formik.values.loginUsername || !formik.values.loginPassword
										}
										onClick={formik.handleSubmit}
										type='submit'>
										{isLoading ? <Spinner isSmall inButton isGrow /> : 'Accedi'}
									</Button>

									<div className='mt-4 text-center'>
										<p className='help-text'>
											Problemi con l'accesso?{' '}
											<a href='mailto:leads2com@gmail.com' className='support-link'>
												Contatta il supporto
											</a>
										</p>
									</div>
								</form>
							</CardBody>
						</Card>
					</div>
				</div>
			</Page>
		</PageWrapper>
	);
};

export default Login;
